import type { ComposerMode } from '@/features/create-post/types';
import type { AuthUser } from '@/store/authStore';

const FULL_COMPOSER_MODES: readonly ComposerMode[] = ['post', 'question', 'event'];
const POST_ONLY_COMPOSER_MODES: readonly ComposerMode[] = ['post'];

/**
 * MyU user type enum from backend payloads:
 *   1=Faculty, 2=Student, 3=Club/Management, 4=Promoter, 5=Parent, 6=Tutor
 */
export const FACULTY_USER_TYPE_ID = 1;
export const MANAGEMENT_USER_TYPE_ID = 3;
export const STUDENT_USER_TYPE_ID = 2;
export const PARENT_USER_TYPE_ID = 5;

function normalizeRole(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function resolveUserTypeId(raw: Record<string, unknown> | null | undefined): number | null {
  if (!raw) return null;
  const nestedUser =
    raw.user && typeof raw.user === 'object' ? (raw.user as Record<string, unknown>) : null;
  const val =
    raw.userTypeId ??
    raw.user_type_id ??
    raw.typeId ??
    raw.userType ??
    nestedUser?.userTypeId ??
    nestedUser?.user_type_id ??
    nestedUser?.typeId ??
    nestedUser?.userType;
  const nestedType = val && typeof val === 'object' ? (val as Record<string, unknown>) : null;
  const numeric = Number(
    nestedType?.id ?? nestedType?.userTypeId ?? nestedType?.typeId ?? nestedType?.value ?? val,
  );
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

/**
 * Returns true when the account should be treated as a teacher/faculty or management
 * account (allowed to create question/event posts).
 */
export function isTeacherOrManagement(
  raw: Record<string, unknown> | null | undefined,
  authUser?: AuthUser | null,
): boolean {
  const numericUserType = resolveUserTypeId(raw);
  if (numericUserType != null) {
    return numericUserType === FACULTY_USER_TYPE_ID || numericUserType === MANAGEMENT_USER_TYPE_ID;
  }
  const role = normalizeRole(authUser?.role);
  // Accept a few common role strings that map to teaching/management
  return role === 'teacher' || role === 'faculty' || role === 'instructor' || role === 'management';
}

/**
 * Returns true when the account is a parent user (userTypeId === 5).
 * Parent users are not allowed to create posts.
 */
export function isParentUser(
  raw: Record<string, unknown> | null | undefined,
  authUser?: AuthUser | null,
): boolean {
  const numericUserType = resolveUserTypeId(raw);
  if (numericUserType != null) {
    return numericUserType === PARENT_USER_TYPE_ID;
  }
  const role = normalizeRole(authUser?.role);
  return role === 'parent' || role === 'parents';
}

export function resolveAllowedComposerModes(
  raw: Record<string, unknown> | null | undefined,
  authUser?: AuthUser | null,
): readonly ComposerMode[] {
  return isTeacherOrManagement(raw, authUser) ? FULL_COMPOSER_MODES : POST_ONLY_COMPOSER_MODES;
}
